import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { EmployeeSetupProvider } from '@/contexts/EmployeeSetupContext';
import EmployeeSetupWizard from '@/components/EmployeeSetupWizard';
import Layout from '@/components/Layout';
import ProtectedRoute, { DefaultRedirect } from './ProtectedRoute';
import GuardedRoute from './GuardedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import { allRouteItems } from '@/config/navConfig';

// ── Páginas públicas (sin auth) ───────────────────────────────────────────────
const Login                   = lazy(() => import('@/modules/platform/pages/Login'));
const HostConfirmation        = lazy(() => import('@/modules/public/pages/HostConfirmation'));
const PublicBooking           = lazy(() => import('@/modules/public/pages/PublicBooking'));
const PublicBookingAttendees  = lazy(() => import('@/modules/public/pages/PublicBookingAttendees'));
const VisitorInvitationBooking = lazy(() => import('@/modules/public/pages/VisitorInvitationBooking'));
const BiometricEnrollment     = lazy(() => import('@/modules/credentials/pages/BiometricEnrollment'));
const ChangePinPage           = lazy(() => import('@/modules/public/pages/ChangePinPage'));
const AttendeePass            = lazy(() => import('@/modules/public/pages/AttendeePass'));
const EmployeePass            = lazy(() => import('@/modules/public/pages/EmployeePass'));
const VisitorPass             = lazy(() => import('@/modules/public/pages/VisitorPass'));
const TenantActivation        = lazy(() => import('@/modules/platform/pages/TenantActivation'));
const ResetPassword           = lazy(() => import('@/modules/platform/pages/ResetPassword'));

function PageLoader() {
  return (
    <div className="flex justify-center items-center h-full min-h-[50vh]">
      <Loader2 size={32} className="animate-spin text-brand" />
    </div>
  );
}

export default function AppRoutes() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Públicas */}
        <Route path="/login" element={isAuthenticated ? <DefaultRedirect /> : <Login />} />
        <Route path="/confirm-visit/:token" element={<HostConfirmation />} />
        <Route path="/reservas/invitados/:token" element={<PublicBookingAttendees />} />
        <Route path="/reservas/:token" element={<PublicBooking />} />
        <Route path="/visita/invitacion/:token" element={<VisitorInvitationBooking />} />
        <Route path="/enrolamiento-facial/:token" element={<BiometricEnrollment />} />
        <Route path="/cambiar-pin/:token" element={<ChangePinPage />} />
        <Route path="/pase-acceso/:token" element={<AttendeePass />} />
        <Route path="/qr-empleado/:token" element={<EmployeePass />} />
        <Route path="/pase-visitante/:token" element={<VisitorPass />} />
        <Route path="/activar-cuenta/:token" element={<TenantActivation />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Privadas — shell con Layout + wizard de setup */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <EmployeeSetupProvider>
                <EmployeeSetupWizard />
                <Layout />
              </EmployeeSetupProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<DefaultRedirect />} />
          {allRouteItems().map(({ path, element: El, requires }) => (
            <Route
              key={path}
              path={path.replace(/^\//, '')}
              element={
                <GuardedRoute requires={requires}>
                  <ErrorBoundary variant="page" resetKey={location.pathname}>
                    <El />
                  </ErrorBoundary>
                </GuardedRoute>
              }
            />
          ))}
          <Route path="*" element={<DefaultRedirect />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
